/**
 * Confirm Info Verification — main component.
 *
 * Displays consolidated PII (name, DOB, SSN, phone, address) assembled by WE from
 * prior verification steps. User confirms the information is correct.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: PII displayed, waiting for user confirmation
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { logger } from "../../shared/utils/logger";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { useConsumer } from "../ConsumerProvider";
import { getConsumerConfig } from "../../consumers";
import styles from "./ConfirmInfoVerification.module.scss";

export type ConfirmInfoVerificationState = "initializing" | "ready";

interface ConfirmInfoAddress {
  number?: string;
  street?: string;
  postalCode?: string;
  municipality?: string;
  subdivisionIso2?: string;
}

interface InitPayload {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  ssn?: string;
  phone?: string;
  address?: ConfirmInfoAddress;
}

function formatAddress(address: ConfirmInfoAddress): string {
  const street = [address.number, address.street].filter(Boolean).join(" ");
  const cityState = [address.municipality, address.subdivisionIso2].filter(Boolean).join(" ");
  return [street, cityState, address.postalCode].filter(Boolean).join(", ");
}

function maskSsn(_ssn: string): string {
  return "###-##-####";
}

export function ConfirmInfoVerification() {
  const { t } = useTranslation();
  const { consumer, fragnoLogoEnabled } = useConsumer();
  const consumerConfig = getConsumerConfig(consumer);
  const consumerLogo = consumerConfig?.logo;
  const themeClass = consumerConfig?.themeClass;
  const [state, setState] = useState<ConfirmInfoVerificationState>("initializing");
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const [showSsn, setShowSsn] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string | null>(null);

  // Best-effort parent origin from document.referrer on mount.
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[ConfirmInfo] Parent origin set from referrer:", origin);
      } else {
        logger.warn("[ConfirmInfo] Referrer not in allowlist:", origin);
      }
    } catch {
      logger.warn("[ConfirmInfo] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  const handleConfirm = useCallback(() => {
    if (!checked) {
      setShowCheckboxError(true);
      return;
    }
    const data = initDataRef.current;
    const targetOrigin = parentOriginRef.current;
    if (!data || !targetOrigin) return;
    logger.log("[ConfirmInfo] User confirmed information");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: { info_confirmed: true },
      },
      targetOrigin
    );
  }, [checked]);

  const handleMakeChanges = useCallback(() => {
    const targetOrigin = parentOriginRef.current;
    if (!targetOrigin) return;
    logger.log("[ConfirmInfo] User requested make changes");
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_MAKE_CHANGES",
        payload: {},
      },
      targetOrigin
    );
  }, []);

  // Send IDME_READY, then listen for IDME_INIT.
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      "*"
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[ConfirmInfo] IDME_INIT rejected from origin:", event.origin);
          return;
        }
        parentOriginRef.current = event.origin;
        logger.log("[ConfirmInfo] Parent origin locked from IDME_INIT:", event.origin);
      } catch {
        logger.warn("[ConfirmInfo] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[ConfirmInfo] Received init");
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <div className={[styles.pageContainer, themeClass].filter(Boolean).join(" ")}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
            </div>
            {consumerLogo && (
              <>
                <span className={styles.logoSeparator} aria-label="and">+</span>
                <div className={styles.logo}>
                  <img src={consumerLogo.src} alt={consumerLogo.alt} />
                </div>
              </>
            )}
          </header>
        )}

        <main className={styles.contentContainer}>
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {state === "ready" && (
            <div className={styles.readyCard}>
              <h1 className={styles.title}>
                {initData?.title ?? t("confirmInfo.title")}
              </h1>

              <p className={styles.description}>
                {t("confirmInfo.description")}
              </p>

              <ul className={styles.infoCard}>
                {initData?.firstName && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.firstName")}</span>
                    <span className={styles.infoValue}>{initData.firstName}</span>
                  </li>
                )}
                {initData?.middleName && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.middleName")}</span>
                    <span className={styles.infoValue}>{initData.middleName}</span>
                  </li>
                )}
                {initData?.lastName && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.lastName")}</span>
                    <span className={styles.infoValue}>{initData.lastName}</span>
                  </li>
                )}
                {initData?.dateOfBirth && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.dateOfBirth")}</span>
                    <span className={styles.infoValue}>{initData.dateOfBirth}</span>
                  </li>
                )}
                {initData?.ssn && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.ssn")}</span>
                    <div className={styles.ssnRow}>
                      <span aria-hidden="true" className={styles.infoValue}>
                        {showSsn ? initData.ssn : maskSsn(initData.ssn)}
                      </span>
                      {/* Screen-reader-readable value. NOT a live region: it must not
                          auto-announce the SSN aloud when revealed. The SSN is read on
                          demand (parity with the sighted, masked view); the toggle's
                          aria-expanded conveys shown/hidden state. Empty when hidden so
                          the masked placeholder is not spoken character-by-character. */}
                      <span className={styles.srOnly}>
                        {showSsn ? initData.ssn : ""}
                      </span>
                      <button
                        type="button"
                        className={styles.showHideBtn}
                        onClick={() => setShowSsn((v) => !v)}
                        aria-label={showSsn ? t("confirmInfo.hideSsnAriaLabel") : t("confirmInfo.showSsnAriaLabel")}
                        aria-expanded={showSsn}
                      >
                        {showSsn ? t("confirmInfo.hideSsn") : t("confirmInfo.showSsn")}
                      </button>
                    </div>
                  </li>
                )}
                {initData?.address && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.address")}</span>
                    <span className={styles.infoValue}>{formatAddress(initData.address)}</span>
                  </li>
                )}
                {initData?.phone && (
                  <li className={styles.infoRow}>
                    <span className={styles.infoLabel}>{t("confirmInfo.fields.phone")}</span>
                    <span className={styles.infoValue}>{initData.phone}</span>
                  </li>
                )}
              </ul>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className={`${styles.checkbox} ${showCheckboxError ? styles.checkboxInputError : ""}`}
                  checked={checked}
                  onChange={(e) => {
                    setChecked(e.target.checked);
                    if (e.target.checked) setShowCheckboxError(false);
                  }}
                />
                <span className={styles.checkboxText}>
                  <Trans
                    i18nKey="confirmInfo.checkbox"
                    components={{
                      1: (
                        <a
                          href="https://www.id.me/fcra"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.fcraLink}
                        />
                      ),
                    }}
                  />
                </span>
              </label>
              {showCheckboxError && (
                <p className={styles.checkboxError}>
                  {t("confirmInfo.checkboxError")}
                </p>
              )}

              <button
                className={styles.confirmButton}
                onClick={handleConfirm}
                type="button"
              >
                {t("confirmInfo.confirmButton")}
              </button>

              <div className={styles.makeChangesWrapper}>
                <button
                  type="button"
                  className={styles.makeChangesButton}
                  onClick={handleMakeChanges}
                >
                  {t("confirmInfo.makeChanges")}
                </button>
              </div>
            </div>
          )}
        </main>

        <footer className={styles.footer}>
          <LanguageSelector />
          <div className={styles.footerLinks}>
            <a
              href="https://www.id.me/about"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLink}
              aria-label={t("confirmInfo.footer.whatIsIdmeAriaLabel")}
            >
              {t("confirmInfo.footer.whatIsIdme")}
            </a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a
              href="https://www.id.me/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLink}
              aria-label={t("confirmInfo.footer.termsOfServiceAriaLabel")}
            >
              {t("confirmInfo.footer.termsOfService")}
            </a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a
              href="https://www.id.me/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLink}
              aria-label={t("confirmInfo.footer.privacyPolicyAriaLabel")}
            >
              {t("confirmInfo.footer.privacyPolicy")}
            </a>
          </div>
        </footer>

      </div>
    </div>
  );
}
